import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PageEditorDropdown from "./PageEditorDropdown";
import CreatePageForm from "./CreatePageForm";
import { PlusIcon, ChevronLeftIcon, ChevronRightIcon, CheckIcon } from "../../design-system/icons";
import { Z_INDEX } from '../../core/utils/zIndexScale';
import { typography, surfaces, borders, textColors, brandColors } from "../../design-system/tokens";

const PageNavigationEditor = ({ 
  currentPage, 
  pages, 
  website, 
  websiteId, 
  username, 
  domain, 
  version_slug,
  isActive,
  generateEditUrl,
  createPageData,
  setCreatePageData,
  createPageError,
  setCreatePageError,
  isCreatingPage,
  hasNavbar,
  hasLeftSidebar,
  hasRightSidebar,
  onCreatePageSubmit,
  onCreatePageCancel,
  forceFullLayout = false,
  forceSimplifiedLayout = false,
  compactControls = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [createPageDropdownOpen, setCreatePageDropdownOpen] = useState(false);
  const [pageDropdownOpen, setPageDropdownOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const pageDropdownRef = useRef(null);
  const createPageDropdownRef = useRef(null);

  const getPageLayout = () => {
    if (!currentPage) {
      return { layout: 'none', prevPage: null, nextPage: null, otherPage: null };
    }

    if (pages.length <= 1) {
      return { layout: 'single', prevPage: null, nextPage: null, otherPage: null };
    }

    const currentIndex = pages.findIndex(page => {
      const pageUrl = generateEditUrl(username, domain, version_slug, page.subdomain);
      return pageUrl === location.pathname;
    });

    if (currentIndex === -1) {
      return { layout: 'none', prevPage: null, nextPage: null, otherPage: null };
    }

    if (pages.length === 2) {
      const otherPage = pages[currentIndex === 0 ? 1 : 0];
      return { layout: 'two-pages', prevPage: null, nextPage: null, otherPage };
    }

    if (pages.length === 3) {
      const prevIndex = currentIndex === 0 ? pages.length - 1 : currentIndex - 1;
      const nextIndex = currentIndex === pages.length - 1 ? 0 : currentIndex + 1;
      
      const prevPage = pages[prevIndex];
      const nextPage = pages[nextIndex];

      return { layout: 'carousel', prevPage, nextPage, otherPage: null };
    }

    // For 4+ pages, show prev, current, next, and "..." dropdown
    const prevIndex = currentIndex === 0 ? pages.length - 1 : currentIndex - 1;
    const nextIndex = currentIndex === pages.length - 1 ? 0 : currentIndex + 1;
    
    const prevPage = pages[prevIndex];
    const nextPage = pages[nextIndex];

    return { layout: 'many-pages', prevPage, nextPage, otherPage: null };
  };

  const { layout, prevPage, nextPage, otherPage } = getPageLayout();

  // Close page dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pageDropdownRef.current && !pageDropdownRef.current.contains(event.target)) {
        setPageDropdownOpen(false);
      }
    };

    if (pageDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [pageDropdownOpen]);

  // Close create page dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (createPageDropdownRef.current && !createPageDropdownRef.current.contains(event.target)) {
        setCreatePageDropdownOpen(false);
        onCreatePageCancel();
      }
    };

    if (createPageDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [createPageDropdownOpen, onCreatePageCancel]);

  const handlePageNavigation = (page) => {
    if (isAnimating || !page) return;
    
    setIsAnimating(true);
    const newUrl = generateEditUrl(username, domain, version_slug, page.subdomain);
    
    setTimeout(() => {
      navigate(newUrl);
      setIsAnimating(false);
    }, 200);
  };

  const renderTwoPageLayout = () => (
    <div className={`${surfaces.primary} rounded-lg shadow-sm border ${borders.default} flex items-center transition-all duration-300 ${
      isAnimating ? 'scale-95 opacity-75' : 'scale-100 opacity-100'
    }`}>
      <button
        onClick={() => handlePageNavigation(otherPage)}
        disabled={isAnimating}
        className={`px-3 sm:px-4 py-2 text-sm font-medium ${textColors.secondary} ${textColors.hover} hover:bg-blue-50 hover:dark:bg-blue-900/20 transition-all duration-200 border-r border-gray-100 dark:border-gray-700 rounded-l-lg ${
          isAnimating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        }`}
      >
        <span className="truncate max-w-20 sm:max-w-32">
          {otherPage.name || otherPage.subdomain || 'Page'}
        </span>
      </button>

      <div className="relative">
        <PageEditorDropdown
          currentPage={currentPage}
          website={website}
          websiteId={websiteId}
          username={username}
          domain={domain}
          version_slug={version_slug}
          isHighlighted={true}
          isLeftEdge={false}
          isRightEdge={true}
        />
      </div>
    </div>
  );

  const renderCarouselLayout = () => (
    <div className={`${surfaces.primary} rounded-lg shadow-sm border ${borders.default} flex items-center transition-all duration-300 ${
      isAnimating ? 'scale-95 opacity-75' : 'scale-100 opacity-100'
    }`}>
      {prevPage && (
        <button
          onClick={() => handlePageNavigation(prevPage)}
          disabled={isAnimating}
          className={`px-3 sm:px-4 py-2 text-sm font-medium ${textColors.secondary} ${textColors.hover} hover:bg-blue-50 hover:dark:bg-blue-900/20 transition-all duration-200 border-r border-gray-100 dark:border-gray-700 rounded-l-lg ${
            isAnimating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          }`}
        >
          <div className="flex items-center space-x-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="truncate max-w-16 sm:max-w-24">
              {prevPage.name || prevPage.subdomain || 'Page'}
            </span>
          </div>
        </button>
      )}

      <div className="relative">
        <PageEditorDropdown
          currentPage={currentPage}
          website={website}
          websiteId={websiteId}
          username={username}
          domain={domain}
          version_slug={version_slug}
          isHighlighted={true}
          isLeftEdge={!prevPage}
          isRightEdge={!nextPage}
        />
      </div>

      {nextPage && (
        <button
          onClick={() => handlePageNavigation(nextPage)}
          disabled={isAnimating}
          className={`px-3 sm:px-4 py-2 text-sm font-medium ${textColors.secondary} ${textColors.hover} hover:bg-blue-50 hover:dark:bg-blue-900/20 transition-all duration-200 border-l border-gray-100 dark:border-gray-700 rounded-r-lg ${
            isAnimating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          }`}
        >
          <div className="flex items-center space-x-1">
            <span className="truncate max-w-16 sm:max-w-24">
              {nextPage.name || nextPage.subdomain || 'Page'}
            </span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </button>
      )}
    </div>
  );

  const renderManyPagesLayout = () => (
    <div className={`${surfaces.primary} rounded-lg shadow-sm border ${borders.default} flex items-center transition-all duration-300 ${
      isAnimating ? 'scale-95 opacity-75' : 'scale-100 opacity-100'
    }`}>
      {prevPage && (
        <button
          onClick={() => handlePageNavigation(prevPage)}
          disabled={isAnimating}
          className={`px-3 sm:px-4 py-2 text-sm font-medium ${textColors.secondary} ${textColors.hover} hover:bg-blue-50 hover:dark:bg-blue-900/20 transition-all duration-200 border-r border-gray-100 dark:border-gray-700 rounded-l-lg ${
            isAnimating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          }`}
        >
          <div className="flex items-center space-x-1">
            <ChevronLeftIcon size="sm" />
            <span className="truncate max-w-16 sm:max-w-24">
              {prevPage.name || prevPage.subdomain || 'Page'}
            </span>
          </div>
        </button>
      )}

      <div className="relative">
        <PageEditorDropdown
          currentPage={currentPage}
          website={website}
          websiteId={websiteId}
          username={username}
          domain={domain}
          version_slug={version_slug}
          isHighlighted={true}
          isLeftEdge={!prevPage}
          isRightEdge={false}
        />
      </div>

      {nextPage && (
        <button
          onClick={() => handlePageNavigation(nextPage)}
          disabled={isAnimating}
          className={`px-3 sm:px-4 py-2 text-sm font-medium ${textColors.secondary} ${textColors.hover} hover:bg-blue-50 hover:dark:bg-blue-900/20 transition-all duration-200 border-l border-gray-100 dark:border-gray-700 ${
            isAnimating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          }`}
        >
          <div className="flex items-center space-x-1">
            <span className="truncate max-w-16 sm:max-w-24">
              {nextPage.name || nextPage.subdomain || 'Page'}
            </span>
            <ChevronRightIcon size="sm" />
          </div>
        </button>
      )}

      {/* More pages dropdown */}
      <div className="relative" ref={pageDropdownRef}>
        <button
          onClick={() => setPageDropdownOpen(!pageDropdownOpen)}
          disabled={isAnimating}
          className={`px-3 py-2 text-sm font-medium ${textColors.secondary} ${textColors.hover} hover:bg-blue-50 hover:dark:bg-blue-900/20 transition-all duration-200 border-l border-gray-100 dark:border-gray-700 rounded-r-lg ${
            isAnimating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          }`}
          title="More pages"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h.01M12 12h.01M19 12h.01" />
          </svg>
        </button>

        {pageDropdownOpen && (
          <div className={`absolute top-full right-0 mt-2 w-[min(16rem,calc(100vw-24px))] sm:w-64 ${surfaces.primary} rounded-lg shadow-xl border ${borders.default} max-h-80 overflow-y-auto`} style={{ zIndex: Z_INDEX.DROPDOWN }}>
            <div className="p-2">
              <h3 className={`${typography.bodySmall} font-medium ${textColors.primary} px-3 py-2 border-b border-gray-100 dark:border-gray-700`}>
                All Pages ({pages.length})
              </h3>
              <div className="mt-2 space-y-1">
                {pages.map((page) => {
                  const isCurrentPage = page.id === currentPage?.id;
                  
                  return (
                    <button
                      key={page.id}
                      onClick={() => {
                        setPageDropdownOpen(false);
                        if (!isCurrentPage) {
                          handlePageNavigation(page);
                        }
                      }}
                      className={`w-full text-left px-3 py-2 rounded-md ${typography.bodySmall} transition-colors ${
                        isCurrentPage 
                          ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium' 
                          : `${textColors.primary} ${surfaces.hover} ${textColors.hover}`
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex flex-col">
                          <span className="truncate max-w-40">
                            {page.name || page.subdomain || 'Untitled Page'}
                          </span>
                          {page.name && page.subdomain && (
                            <span className={`${typography.caption} truncate max-w-40`}>
                              /{page.subdomain}
                            </span>
                          )}
                        </div>
                        {isCurrentPage && (
                          <CheckIcon size="sm" color="primary" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderSinglePageLayout = () => (
    <div className={`${surfaces.primary} rounded-lg shadow-sm border ${borders.default} flex items-center transition-all duration-300 ${
      isAnimating ? 'scale-95 opacity-75' : 'scale-100 opacity-100'
    }`}>
      <div className="relative">
        <PageEditorDropdown
          currentPage={currentPage}
          website={website}
          websiteId={websiteId}
          username={username}
          domain={domain}
          version_slug={version_slug}
          isHighlighted={true}
          isLeftEdge={true}
          isRightEdge={true}
        />
      </div>
    </div>
  );

  const renderSimplifiedLayout = () => (
    <div className={`${surfaces.primary} rounded-lg shadow-sm border ${borders.default} flex items-center transition-all duration-300 ${
      isAnimating ? 'scale-95 opacity-75' : 'scale-100 opacity-100'
    }`}>
      <div className="relative">
        <PageEditorDropdown
          currentPage={currentPage}
          website={website}
          websiteId={websiteId}
          username={username}
          domain={domain}
          version_slug={version_slug}
          isHighlighted={true}
          isLeftEdge={true}
          isRightEdge={false}
        />
      </div>

      {/* More pages dropdown - always show for multiple pages */}
      {pages.length > 1 && (
        <div className="relative" ref={pageDropdownRef}>
          <button
            onClick={() => setPageDropdownOpen(!pageDropdownOpen)}
            disabled={isAnimating}
            className={`px-3 py-2 text-sm font-medium ${textColors.secondary} ${textColors.hover} hover:bg-blue-50 hover:dark:bg-blue-900/20 transition-all duration-200 border-l border-gray-100 dark:border-gray-700 rounded-r-lg ${
              isAnimating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
            }`}
            title="More pages"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h.01M12 12h.01M19 12h.01" />
            </svg>
          </button>

          {pageDropdownOpen && (
            <div className={`absolute top-full right-0 mt-2 w-[min(16rem,calc(100vw-24px))] sm:w-64 ${surfaces.primary} rounded-lg shadow-xl border ${borders.default} max-h-80 overflow-y-auto`} style={{ zIndex: Z_INDEX.DROPDOWN }}>
              <div className="p-2">
                <h3 className={`${typography.bodySmall} font-medium ${textColors.primary} px-3 py-2 border-b border-gray-100 dark:border-gray-700`}>
                  All Pages ({pages.length})
                </h3>
                <div className="mt-2 space-y-1">
                  {pages.map((page) => {
                    const isCurrentPage = page.id === currentPage?.id;
                    
                    return (
                      <button
                        key={page.id}
                        onClick={() => {
                          setPageDropdownOpen(false);
                          if (!isCurrentPage) {
                            handlePageNavigation(page);
                          }
                        }}
                        className={`w-full text-left px-3 py-2 rounded-md ${typography.bodySmall} transition-colors ${
                          isCurrentPage 
                            ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium' 
                            : `${textColors.primary} ${surfaces.hover} ${textColors.hover}`
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex flex-col">
                            <span className="truncate max-w-40">
                              {page.name || page.subdomain || 'Untitled Page'}
                            </span>
                            {page.name && page.subdomain && (
                              <span className={`${typography.caption} truncate max-w-40`}>
                                /{page.subdomain}
                              </span>
                            )}
                          </div>
                          {isCurrentPage && (
                            <CheckIcon size="sm" color="primary" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="flex items-center space-x-1">
      {forceFullLayout ? (
        /* When forced (e.g. tablet second row), always show full layout */
        <div>
          {layout === 'two-pages' && renderTwoPageLayout()}
          {layout === 'carousel' && renderCarouselLayout()}
          {layout === 'many-pages' && renderManyPagesLayout()}
          {layout === 'single' && renderSinglePageLayout()}
        </div>
      ) : forceSimplifiedLayout ? (
        /* When forced simplified, always show current page + dots dropdown */
        <div>
          {layout === 'single' ? renderSinglePageLayout() : renderSimplifiedLayout()}
        </div>
      ) : (
        <>
          {/* Simplified layout for screens smaller than xl */}
          <div className="xl:hidden">
            {layout === 'single' ? renderSinglePageLayout() : renderSimplifiedLayout()}
          </div>

          {/* Full carousel layout for xl screens and above */}
          <div className="hidden xl:block">
            {layout === 'two-pages' && renderTwoPageLayout()}
            {layout === 'carousel' && renderCarouselLayout()}
            {layout === 'many-pages' && renderManyPagesLayout()}
            {layout === 'single' && renderSinglePageLayout()}
          </div>
        </>
      )}

      <div className="relative" ref={createPageDropdownRef}>
        <button
          onClick={() => setCreatePageDropdownOpen(!createPageDropdownOpen)}
          disabled={isCreatingPage}
          className={[
            compactControls ? "p-1 sm:p-1.5" : "p-2",
            "text-white rounded-lg hover:bg-white hover:bg-opacity-10 transition-colors",
            "disabled:opacity-50 disabled:cursor-not-allowed",
          ].join(" ")}
          title="Create new page"
        >
          <PlusIcon size={compactControls ? "sm" : "md"} />
        </button>
        
        {createPageDropdownOpen && (
          <div
            className={[
              compactControls
                ? "fixed left-1/2 top-14 -translate-x-1/2"
                : "absolute right-0 mt-2",
              `w-[min(24rem,calc(100vw-24px))] sm:w-96 ${surfaces.primary}`,
              `rounded-lg shadow-xl border ${borders.default}`,
              "max-h-[calc(100vh-6rem)] overflow-y-auto overscroll-contain",
            ].join(" ")}
            style={{ zIndex: Z_INDEX.DROPDOWN }}
          >
            <CreatePageForm 
              createPageData={createPageData}
              setCreatePageData={setCreatePageData}
              createPageError={createPageError}
              setCreatePageError={setCreatePageError}
              isCreatingPage={isCreatingPage}
              hasNavbar={hasNavbar}
              hasLeftSidebar={hasLeftSidebar}
              hasRightSidebar={hasRightSidebar}
              onSubmit={onCreatePageSubmit}
              onCancel={() => {
                setCreatePageDropdownOpen(false);
                onCreatePageCancel();
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PageNavigationEditor; 